// components/CookieModal.jsx
"use client";
import { useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useLocation } from 'react-router-dom';

// Flag globale per tracciare lo stato di Iubenda (evita loop)
let iubendaState = {
  isSetup: false,
  isSettingUp: false,
  lastAction: null,
  lastPathname: null
};

// Passa la scelta del banner a Consent Mode di Google e, solo col marketing,
// accende i pixel. Tutte e due le funzioni vivono in public/index.html e
// rileggono `window._iub.cs.consent.purposes`: qui non si decide niente.
export const applicaSceltaCookie = () => {
  if (typeof window === 'undefined') return;
  if (typeof window.__aggiornaConsensoGoogle === 'function') {
    window.__aggiornaConsensoGoogle();
  }
  if (typeof window.__loadMarketingPixels === 'function') {
    window.__loadMarketingPixels();
  }
};

const CookieSolution = () => {
  const location = useLocation();
  const setupTimeoutRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();

    // Iubenda su TUTTE le rotte (STU-480). Prima solo su sette rotte pubbliche
    // (/, /landing, /signIn, /signUp, /verifica-email, /welcome,
    // /payment-completed) e per chi era loggato: con Google Analytics che
    // parte negato, chi arrivava su /maturita, /blog, /piani-e-prezzi o
    // /sicurezza non vedeva mai il banner e non poteva dare il consenso.

    // Evita operazioni ridondanti se il pathname non è cambiato
    const pathChanged = iubendaState.lastPathname !== location.pathname;
    iubendaState.lastPathname = location.pathname;

    // `removeIubenda` e' stata tolta il 10/09/2026 (STU-326): era codice morto,
    // `handleIubendaVisibility` non l'ha mai chiamata. Era pero' l'unico punto che
    // azzerava `window._iub`, e leggerla faceva credere che il consenso potesse
    // sparire cambiando pagina. Non succede: una volta caricato, Iubenda resta.

    const setupIubenda = () => {
      // Skip se già setup o in corso
      if (iubendaState.isSetup || iubendaState.isSettingUp) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏭️ Iubenda già attivo, skip setup');
        }
        return;
      }

      // Verifica che non ci siano già script presenti
      const existingScripts = document.querySelectorAll('script[src*="iubenda"]');
      if (existingScripts.length > 0) {
        iubendaState.isSetup = true;
        if (process.env.NODE_ENV === 'development') {
          console.log('⏭️ Script Iubenda già presenti, marcato come setup');
        }
        return;
      }

      iubendaState.isSettingUp = true;

      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔧 Setting up Iubenda...');
        }

        // Delay per assicurare DOM pulito
        setupTimeoutRef.current = setTimeout(() => {
          try {
            if (!document.body) {
              iubendaState.isSettingUp = false;
              return;
            }

            // Ricontrolla se già presenti (potrebbe essere cambiato durante il delay)
            const scripts = document.querySelectorAll('script[src*="iubenda"]');
            if (scripts.length > 0) {
              iubendaState.isSetup = true;
              iubendaState.isSettingUp = false;
              return;
            }
            
            // Iubenda scripts configuration
            window._iub = window._iub || [];
            window._iub.csConfiguration = {
              askConsentAtCookiePolicyUpdate: true,
              enableFadp: true,
              enableLgpd: true,
              enableUspr: true,
              fadpApplies: true,
              floatingPreferencesButtonDisplay: "bottom-right",
              lang: "it",
              perPurposeConsent: true,
              // Consent Mode di Google lo aggiorniamo NOI (applicaSceltaCookie
              // -> __aggiornaConsensoGoogle in index.html), dalle finalita'.
              // L'integrazione di Iubenda mandava un suo update con
              // ad_storage GRANTED anche senza la finalita' 5 (visto su
              // preprod l'08/10: con usprApplies il modello USA, a rinuncia,
              // concede la pubblicita' di default) e si accavallava al nostro.
              googleConsentMode: false,
              preferenceCookie: { expireAfter: 180 },
              siteId: 3607864,
              usprApplies: true,
              whitelabel: false,
              cookiePolicyId: 73904869,
              banner: {
                acceptButtonDisplay: true,
                closeButtonDisplay: false,
                customizeButtonDisplay: true,
                explicitWithdrawal: true,
                listPurposes: true,
                ownerName: "studierai.app",
                position: "float-bottom-center",
                rejectButtonDisplay: true,
                showTitle: false,
                showTotalNumberOfProviders: true
              },
              // Le callback passano la scelta del banner a Google Consent Mode
              // (`__aggiornaConsensoGoogle` in index.html: finalita' 4 ->
              // analytics_storage, 5 -> ad_*) e accendono Meta e Spotify solo
              // con la finalita' 5 (`__loadMarketingPixels`). Nessuna callback
              // concede qualcosa che la persona non ha scelto (STU-480).
              callback: {
                onReady: function() {
                  // Visita di ritorno: il cookie Iubenda (180gg) porta gia' la
                  // scelta, quindi si applica subito.
                  try {
                    applicaSceltaCookie();
                  } catch (e) {
                    console.warn('[Iubenda] onReady error:', e);
                  }
                },
                onConsentGiven: function() {
                  applicaSceltaCookie();
                },
                onConsentRejected: function() {
                  // Rifiuto: Consent Mode torna (o resta) tutto negato, e i
                  // pixel non si toccano nemmeno.
                  if (typeof window.__aggiornaConsensoGoogle === 'function') {
                    window.__aggiornaConsensoGoogle();
                  }
                },
                // Copre il caso "prima rifiuto, poi riapro le preferenze e
                // cambio idea" in un senso o nell'altro: onConsentGiven non
                // riscatta, questa si'.
                onPreferenceExpressed: function() {
                  applicaSceltaCookie();
                }
              }
            };

            // Load Iubenda scripts in correct order to avoid race conditions
            // Order: 1) TCF stub, 2) Main script, 3) Autoblocking (after main loads)

            // 1. Load TCF stub first
            const stubScript = document.createElement("script");
            stubScript.src = "//cdn.iubenda.com/cs/tcf/stub-v2.js";
            stubScript.type = "text/javascript";
            stubScript.onerror = (e) => {
              console.error('❌ Errore caricamento Iubenda TCF stub script:', e);
              if (window.Sentry) {
                window.Sentry.captureMessage('Iubenda script load failed: tcf-stub', 'warning');
              }
            };
            document.body.appendChild(stubScript);

            // 2. Load main Iubenda script
            const mainScript = document.createElement("script");
            mainScript.src = "//cdn.iubenda.com/cs/iubenda_cs.js";
            mainScript.type = "text/javascript";
            mainScript.charset = "UTF-8";
            mainScript.async = true;
            mainScript.onerror = (e) => {
              console.error('❌ Errore caricamento Iubenda main script:', e);
              if (window.Sentry) {
                window.Sentry.captureMessage('Iubenda script load failed: main', 'warning');
              }
            };

            // NIENTE AUTOBLOCKING (STU-326, 10/09/2026). Lo script
            // cs.iubenda.com/autoblocking/3607864.js bloccava le richieste GA4 a
            // livello di rete anche con il consenso concesso: e' la ragione per cui
            // il 13/02 tutto Iubenda era stato commentato, banner compreso, e da
            // allora nessuno ha piu' chiesto il consenso a nessuno. Il banner e il
            // consenso per finalita' restano, l'autoblocking no: i pixel di
            // marketing li accende `window.__loadMarketingPixels()` (index.html)
            // solo dopo il consenso, che e' lo stesso risultato senza rompere GA4.

            document.body.appendChild(mainScript);

            // Marca come setup completato
            iubendaState.isSetup = true;
            iubendaState.isSettingUp = false;
            iubendaState.lastAction = 'setup';

            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Iubenda scripts caricati con successo');
            }
          } catch (innerError) {
            iubendaState.isSettingUp = false;
            console.error('❌ Errore interno setup Iubenda:', innerError);
          }
        }, 250);
      } catch (error) {
        iubendaState.isSettingUp = false;
        console.error('❌ Errore setup Iubenda:', error);
      }
    };

    // Gestisci la visibilità di Iubenda (con debounce implicito tramite state).
    // Il banner compare finche' la persona non ha scelto, poi resta solo il
    // pulsante delle preferenze. setupIubenda e' idempotente.
    //
    // Essere loggati NON e' un consenso (STU-480). Prima qui un utente con
    // l'email verificata si trovava il consenso concesso in automatico, senza
    // aver mai visto il banner. Ora vale solo la scelta fatta nel banner.
    const handleIubendaVisibility = () => {
      setupIubenda();
    };

    // Ascolta i cambiamenti di autenticazione (solo se il path è cambiato per evitare loop)
    const unsubscribe = onAuthStateChanged(auth, () => {
      // Usa un piccolo delay per evitare race condition con il routing
      setTimeout(() => handleIubendaVisibility(), 100);
    });

    // Gestisci la visibilità iniziale (con delay per evitare conflitti)
    setTimeout(() => handleIubendaVisibility(), 150);

    // Cleanup
    return () => {
      unsubscribe();
      // Iubenda non si smonta: il consenso deve restare disponibile ovunque,
      // compreso il pulsante per revocarlo.
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  return null;
};

export default CookieSolution;
